import { Menu, Tooltip } from '@mantine/core';
import { CircleHelp } from 'lucide-react';
import { useLanguage } from '@contexts/LanguageContext';
import { useTheme } from '@contexts/ThemeContext';
import { resolveTourCopy } from './tour-copy';
import { GLOBAL_TOUR_ID, tourById } from './tour-registry';
import { useOptionalTour } from './TourProvider';
import { useOptionalOnboardingGate } from '@/features/first-login/gate-context';

interface TourHelpButtonProps {
  /** Sidebar rail is collapsed: icon only, tooltip carries the label. */
  isMinimized?: boolean;
  /** Shared with the sidebar's other footer controls so it looks native. */
  className?: string;
  /**
   * `toolbar` for pages that hide the sidebar entirely — the PIB editor, the
   * review screen, export. Those are exactly the screens with the most tours,
   * and the sidebar-docked control is simply not on them.
   */
  variant?: 'sidebar' | 'toolbar';
  /** Emit the `tour-help-pill` anchor. A preview copy of the rail passes false. */
  withAnchor?: boolean;
}

/**
 * "Panduan" — replays the guidance for whatever the user is looking at.
 *
 * Docked in the sidebar footer next to the theme and language controls, rather
 * than floating over the page. A floating pill has to dodge whatever the page
 * puts in the corner, and it lost that fight repeatedly: it covered Process
 * Dokumen on the upload screen, and once it moved clear of the action bar it
 * simply hovered over the panel content instead. A docked control cannot
 * collide with anything, and it sits where users already look for app-level
 * switches.
 */
export const TourHelpButton = ({
  isMinimized = false,
  className,
  variant = 'sidebar',
  withAnchor = true,
}: TourHelpButtonProps) => {
  const tour = useOptionalTour();
  const onboardingGate = useOptionalOnboardingGate();
  const { language } = useLanguage();
  const { colors: themeColors } = useTheme();

  if (!tour) {
    return null;
  }

  const { pageTour, subTour, subTourLabel, sections, startTour, startTourAt, startTourById } = tour;
  const globalTour = tourById(GLOBAL_TOUR_ID);
  const t = (key: string, vars?: Record<string, string | number>) => resolveTourCopy(language, key, vars);

  const itemStyle = { color: themeColors.text };

  const isToolbar = variant === 'toolbar';

  return (
    <Menu
      position={isToolbar ? 'bottom-end' : isMinimized ? 'right' : 'top-end'}
      offset={8}
      shadow="md"
      radius="md"
      width={240}
    >
      <Menu.Target>
        <Tooltip
          label={t('pill.label')}
          position={isToolbar ? 'bottom' : 'right'}
          disabled={!isMinimized && !isToolbar}
          withArrow
        >
          <button
            type="button"
            // Anchor only on the sidebar copy. Pages that "hide" the sidebar do
            // it with display:none, so its button stays in the DOM — tagging
            // both would give querySelector two matches and let a tour step
            // spotlight the invisible one.
            {...(isToolbar || !withAnchor ? {} : { 'data-tour': 'tour-help-pill' })}
            aria-label={t('pill.aria')}
            className={
              isToolbar ? 'inline-flex items-center justify-center rounded-[10px] border transition-colors' : className
            }
            style={
              isToolbar
                ? {
                    width: 36,
                    height: 36,
                    color: themeColors.textSecondary,
                    borderColor: themeColors.border,
                    backgroundColor: themeColors.card,
                  }
                : { color: themeColors.text, backgroundColor: 'transparent' }
            }
          >
            <CircleHelp size={isToolbar ? 16 : 15} strokeWidth={1.5} className="shrink-0" />
            {!isMinimized && !isToolbar && (
              <span className="text-[13px] truncate flex-1 text-left">{t('pill.label')}</span>
            )}
          </button>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown
        style={{ backgroundColor: themeColors.card, borderColor: themeColors.border, color: themeColors.text }}
      >
        {subTour && (
          <Menu.Item onClick={() => startTour(subTour)} style={itemStyle}>
            {t('pill.tabGuide', { tab: subTourLabel ?? '' })}
          </Menu.Item>
        )}
        {pageTour && (
          <Menu.Item onClick={() => startTour(pageTour)} style={itemStyle}>
            {t('pill.pageGuide')}
          </Menu.Item>
        )}
        {/* Entry points partway into the page's tour: a user who only wants the
            split explained should not sit through the upload walkthrough again. */}
        {pageTour &&
          sections.map((section) => (
            <Menu.Item key={section.key} onClick={() => startTourAt(pageTour, section.stepKey)} style={itemStyle}>
              {t(`pill.section.${section.key}`)}
            </Menu.Item>
          ))}
        {globalTour && (
          <Menu.Item onClick={() => startTourById(GLOBAL_TOUR_ID)} style={{ color: themeColors.textSecondary }}>
            {t('pill.menuGuide')}
          </Menu.Item>
        )}
        {/* Re-runs the first-login wizard on request, whatever its recorded status. */}
        {onboardingGate && (
          <Menu.Item onClick={onboardingGate.openOnboarding} style={{ color: themeColors.textSecondary }}>
            {t('pill.onboarding')}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  );
};
